import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  NgZone,
  provideAppInitializer,
  ProviderToken,
  TransferState,
} from '@angular/core';
import { registerCatchAndLogErrorReporter } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { type CaptureResult, type PostHogConfig } from 'posthog-js';
import { PostHogErrorHandler } from './posthog.error-handler';
import { PostHogService } from './posthog.service';
import { PostHogApplication } from './posthog-application';
import { POSTHOG_DEVICE_ID, PostHogDeviceId } from './posthog-device-id.token';
import { PostHogBootstrapState, takePostHogBootstrapState } from './posthog-flag-state';
import { POSTHOG_PLATFORM, PostHogPlatform } from './posthog-platform.token';

export interface RmaPostHogOptions {
  /**
   * Merged over the defaults below, so a caller can turn capture behaviour off as well as on.
   *
   * Two are worth knowing before setting them. `capture_pageview` dedupes on pathname, so every app
   * here turns it off and counts `NavigationEnd` instead — otherwise a redirect or a lowercase
   * rewrite counts twice and a query-only navigation not at all. `capture_pageleave` derives from
   * it unless stated, so turning pageview off and saying nothing else loses session duration too.
   */
  config?: Partial<PostHogConfig>;
  /** Off unless asked for: it would otherwise displace an app's own `ErrorHandler` silently. */
  registerErrorHandler?: boolean;
  /**
   * The app's device-id service. Given one, PostHog takes its id as the distinct id instead of
   * generating a private one, so a visitor is one person across every host sharing that cookie.
   * An SSR bootstrap wins where there is one — the server already resolved the same id.
   */
  deviceId?: ProviderToken<PostHogDeviceId>;
  /**
   * Starts recording once the current user is known not to be impersonating, signed out included. Pair it with
   * `disable_session_recording: true`, so nothing records before that point.
   */
  recordSessions?: boolean;
}

export function provideRmaPostHog(
  projectToken: string | undefined,
  host: string | undefined,
  app: PostHogApplication,
  platformService: ProviderToken<PostHogPlatform>,
  options: RmaPostHogOptions = {},
): EnvironmentProviders {
  return makeEnvironmentProviders([
    // Bound once, so route tracking reads the host app's service without an argument of its own.
    { provide: POSTHOG_PLATFORM, useExisting: platformService },
    ...(options.deviceId ? [{ provide: POSTHOG_DEVICE_ID, useExisting: options.deviceId }] : []),
    provideAppInitializer(() => {
      const zone = inject(NgZone);
      const posthogService = inject(PostHogService);
      const platform = inject(POSTHOG_PLATFORM);
      const deviceId = inject(POSTHOG_DEVICE_ID, { optional: true });
      const transferState = inject(TransferState);

      if (!platform.isTrackable) {
        posthogService.markUnavailable();

        return;
      }

      // Absent in an app with no SSR flag evaluation, so this is inert rather than opt-out.
      const bootstrapState = takePostHogBootstrapState(transferState);
      const bootstrap = resolveBootstrap(bootstrapState, deviceId);

      const config: Partial<PostHogConfig> = {
        api_host: host,
        defaults: '2026-05-30',

        // Off only where PostHogErrorHandler takes over, or nothing would report; an unset value would
        // defer to the project's server-side setting, so it is always explicit.
        capture_exceptions: !options.registerErrorHandler,
        capture_performance: true,
        // No `token`: that is PostHog's own project token, which the SDK writes and reads back.
        property_denylist: ['password', 'access_token', 'authorization'],
        before_send: stripLoginTokenQuery,
        ...options.config,
        ...(bootstrap ? { bootstrap } : {}),
      };

      // Keeps the SDK's timers out of change detection.
      zone.runOutsideAngular(() => {
        // Apps that don't record keep the three-argument call their specs assert on.
        if (options.recordSessions) {
          posthogService.init(projectToken, app, config, true);
        } else {
          posthogService.init(projectToken, app, config);
        }

        // Only once the SDK really started, so dev and the server keep logging to the console alone.
        if (posthogService.client) {
          registerCatchAndLogErrorReporter((error) => posthogService.captureException(error));
        }

        // Before the first refresh, so the client re-evaluates against the server's inputs.
        if (bootstrapState) {
          posthogService.client?.setPersonPropertiesForFlags(bootstrapState.personProperties, false);
        }
      });
    }),
    ...(options.registerErrorHandler ? [{ provide: ErrorHandler, useClass: PostHogErrorHandler }] : []),
  ]);
}

const LOGIN_TOKEN_PATHNAME = '/login/token';

// posthog-js copies the landing URL and referrer into these as well, and sends the initial and session-entry
// ones in $set_once.
const URL_PROPERTIES = ['$current_url', '$session_entry_url', '$initial_current_url', '$referrer'];

function stripLoginTokenUrl(value: string): string | undefined {
  // Cheap pre-check, since this runs for every captured event and the path is almost never /login/token.
  if (!value.includes(LOGIN_TOKEN_PATHNAME)) {
    return undefined;
  }

  let url: URL;

  try {
    url = new URL(value);
  } catch {
    return undefined;
  }

  if (url.pathname !== LOGIN_TOKEN_PATHNAME && url.pathname !== `${LOGIN_TOKEN_PATHNAME}/`) {
    return undefined;
  }

  url.search = '';
  url.hash = '';

  return url.toString();
}

// Copies only when something changes, so an untouched record keeps its identity.
function stripLoginTokenFromRecord(record: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  let result = record;

  for (const key of URL_PROPERTIES) {
    const value = record?.[key];
    const stripped = typeof value === 'string' ? stripLoginTokenUrl(value) : undefined;

    if (stripped !== undefined) {
      result = { ...result, [key]: stripped };
    }
  }

  return result;
}

// The login token page carries a credential in its query, and posthog-js stamps URLs on every event.
function stripLoginTokenQuery(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event;
  }

  const properties = stripLoginTokenFromRecord(event.properties);
  const setOnce = stripLoginTokenFromRecord(event.$set_once);
  const set = stripLoginTokenFromRecord(event.$set);

  if (properties === event.properties && setOnce === event.$set_once && set === event.$set) {
    return event;
  }

  return {
    ...event,
    ...(properties && { properties }),
    ...(setOnce && { $set_once: setOnce }),
    ...(set && { $set: set }),
  };
}

/**
 * The server's evaluation where there is one; otherwise the app's own device id, so a visitor is not
 * a new person on every host. Reading it here is also what writes the cookie, which the SSR render
 * depends on finding — nothing else in these apps injects that service.
 */
function resolveBootstrap(state: PostHogBootstrapState | undefined, deviceId: PostHogDeviceId | null): Partial<PostHogConfig>['bootstrap'] {
  if (state) {
    return {
      distinctID: state.distinctId,
      featureFlags: state.featureFlags,
      featureFlagPayloads: state.featureFlagPayloads,
    };
  }

  return deviceId ? { distinctID: deviceId.getDeviceId() } : undefined;
}
